import { createFileRoute } from '@tanstack/react-router';
import { Anchor, Button, Card, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { authApi } from '../../lib/auth-api';

const Schema = z.object({ email: z.string().trim().toLowerCase().email() });

function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof Schema>>({ resolver: zodResolver(Schema) });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await authApi.forgotPassword(values.email);
      setSent(true);
    } catch (err) {
      notifications.show({
        message: (err as Error).message || 'Could not send reset link',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  });

  if (sent) {
    return (
      <Card>
        <Stack gap="sm">
          <Text fw={600}>Check your inbox</Text>
          <Text size="sm" c="dimmed">
            If that email is on file, we've sent a reset link. The link expires in 1 hour.
          </Text>
          <Anchor href="/auth/login" size="sm">
            Back to sign in
          </Anchor>
        </Stack>
      </Card>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <Stack gap="sm">
          <Text fw={600} size="lg">
            Forgot password
          </Text>
          <form onSubmit={onSubmit}>
            <Stack gap="sm">
              <TextInput label="Email" {...register('email')} error={errors.email?.message} />
              <Button type="submit" loading={submitting}>
                Send reset link
              </Button>
            </Stack>
          </form>
          <Anchor href="/auth/login" size="sm">
            Back to sign in
          </Anchor>
        </Stack>
      </Card>
    </motion.div>
  );
}

export const Route = createFileRoute('/auth/forgot-password')({ component: ForgotPassword });
